import { useRef, useState } from "react";
import type { Row } from "../types";
import { api, ApiError } from "../api";
import { IconClose, IconCheck, IconUpload, IconFile } from "../icons";

interface Props {
  treinamento: Row; // linha de treinamento a concluir
  onClose: () => void;
  onConcluded: () => void; // recarrega listas (skill do colaborador muda)
}

function hojeISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Conclui um treinamento: grava a data, marca como "Concluído", adiciona o
 * treinamento às skills do colaborador e envia o certificado (opcional) para a
 * pasta do colaborador.
 */
export default function ConcluirTreinoModal({ treinamento, onClose, onConcluded }: Props) {
  const [data, setData] = useState<string>(
    treinamento.data ? String(treinamento.data).slice(0, 10) : hojeISO(),
  );
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const jaConcluido = treinamento.status === "Concluído";

  async function concluir() {
    setSaving(true);
    setError(null);
    try {
      const fd = new FormData();
      if (data) fd.append("data", data);
      if (file) fd.append("file", file);
      const res = await api.ia.concluirTreinamento(treinamento.id, fd);
      const partes = [`Skill "${res.skill_atualizada}" adicionada ao colaborador.`];
      if (res.certificado) partes.push("Certificado salvo na pasta do colaborador.");
      setOkMsg(partes.join(" "));
      onConcluded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao concluir treinamento");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconCheck size={18} /> Concluir treinamento
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="concluir-resumo">
            <strong>{treinamento.titulo}</strong>
            <span>{treinamento.colaborador_nome || "Colaborador"}</span>
          </div>
          {jaConcluido && (
            <div className="form-ok" style={{ marginBottom: 12 }}>
              Já marcado como concluído — você pode reenviar o certificado ou ajustar a data.
            </div>
          )}

          <div className="form-grid">
            <label className="field">
              <span>Data de conclusão</span>
              <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
            </label>
            <label className="field field-wide">
              <span>Certificado (PDF/imagem, opcional)</span>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              {file && (
                <span className="file-chosen">
                  <IconFile size={15} /> {file.name}
                </span>
              )}
            </label>
          </div>

          <p className="hint">
            <IconUpload size={14} /> Ao concluir, o treinamento entra nas <strong>skills</strong> do
            colaborador e o certificado vai para a <strong>pasta</strong> dele.
          </p>

          {error && <div className="form-error">{error}</div>}
          {okMsg && (
            <div className="form-ok">
              <IconCheck size={16} /> {okMsg}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {okMsg ? "Fechar" : "Cancelar"}
          </button>
          {!okMsg && (
            <button type="button" className="btn-primary" onClick={concluir} disabled={saving}>
              {saving ? "Concluindo…" : "Concluir e atualizar skill"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
